{
  "appMeta": {
    "title": "dTIMS Licensing",
    "description": "dTIMS Licensing — manage organisation and individual licenses across the dTIMS platform."
  },
  "layout": {
    "title": "Licensing",
    "description": "Manage individual and organizational licenses, and configure license plans.",
    "navigation": {
      "home": "Home",
      "licenses": "Licenses",
      "individual": "Individual",
      "organizational": "Organizational",
      "plans": "Plans",
      "roles": "Roles & Permissions"
    }
  },
  "authCallback": {
    "logoAlt": "Deighton logo",
    "exchanging": {
      "title": "Completing sign-in…",
      "subtext": "Exchanging authorisation code. Please wait."
    },
    "authenticated": {
      "title": "Sign-in complete",
      "subtext": "Redirecting you to the application…"
    },
    "processing": {
      "title": "Processing…"
    },
    "error": {
      "title": "Sign-in failed",
      "subtext": "Something went wrong during the sign-in process.",
      "iconLabel": "Error icon",
      "showDetails": "Show technical details",
      "detailsRegionLabel": "Technical error details",
      "retry": "Try again",
      "retryAriaLabel": "Return to login and try again"
    }
  },
  "common": {
    "cancel": "Cancel",
    "save": "Save",
    "close": "Close",
    "edit": "Edit",
    "delete": "Delete",
    "remove": "Remove",
    "add": "Add",
    "undo": "Undo",
    "active": "Active",
    "inactive": "Inactive",
    "enabled": "Enabled",
    "disabled": "Disabled",
    "dismiss": "Dismiss"
  },
  "dashboard": {
    "title": "Dashboard",
    "subtitle": "Overview of your license management",
    "loading": "Loading dashboard data...",
    "error": "Failed to load dashboard data:",
    "sessionExpired": "Your session has expired. Please sign in again to continue.",
    "sessionExpiredAction": "Sign in again",
    "permissionDenied": "You don't have permission to view this data. Contact a Tenant Admin if you need access.",
    "stats": {
      "totalUsers": "Total Users",
      "totalLicenses": "Total Licenses",
      "plans": "Plans",
      "roles": "Roles & Permissions",
      "seatUtilization": "Seat Utilization",
      "active": "active",
      "of": "of",
      "used": "used"
    },
    "buttons": {
      "users": "Users",
      "manageLicenses": "Manage Licenses",
      "managePlans": "Manage Plans",
      "manageRoles": "Manage Roles"
    },
    "expiringCard": {
      "title": "Expiring Soon",
      "days": "days",
      "noExpiring": "No licenses expiring in the next 30 days"
    },
    "distributionCard": {
      "title": "License Distribution",
      "seats": "seats",
      "noData": "No license data available"
    }
  },
  "individualLicense": {
    "title": "User Management",
    "subtitle": "Manage individual user license assignments",
    "dialog": {
      "title": "Individual License",
      "username": "Username",
      "email": "Email",
      "environment": "Environment",
      "displayName": "Display Name",
      "organization": "Organization",
      "assigned": "Assigned",
      "licenseName": "License Name",
      "planType": "Plan Type",
      "currentUsers": "Current Users",
      "maxUsers": "Max Users",
      "activated": "Activated",
      "expires": "Expires",
      "atCapacity": "License is at capacity",
      "noLicenses": "No licenses available for this organization",
      "loadingLicenses": "Loading available licenses..."
    }
  },
  "organizationLicense": {
    "title": "License Management",
    "subtitle": "Manage organization licenses and seat allocations",
    "dialog": {
      "title": "Organization Licenses",
      "clientName": "Client Name",
      "clientId": "Client ID",
      "planName": "Plan Name",
      "licenseLevel": "License Level",
      "licenseKey": "License Key",
      "quantity": "Quantity",
      "startDate": "Start Date",
      "endDate": "End Date",
      "addNewLicense": "Add New License",
      "environment": "Environment",
      "status": "Status",
      "exceedsPlanLimit": "One or more licenses exceed their plan's max user limit. You may reduce but not increase these values."
    },
    "deleteDialog": {
      "title": "Delete License",
      "cannotDelete": "Cannot delete this license",
      "cannotDeleteReasons": "cannot be deleted for the following reasons:",
      "deactivateFirst": "Please deactivate the license and unassign all users before deleting.",
      "confirmTitle": "Delete \"{{planName}}\" for \"{{tenantName}}\"?",
      "confirmMessage": "This license has no assigned users and is not active. This action cannot be undone.",
      "deleteLicense": "Delete License"
    },
    "modulesDialog": {
      "title": "License Features",
      "planName": "Plan Name",
      "planType": "Plan Type",
      "licenseId": "License ID",
      "trial": "Trial",
      "noFeatures": "No features associated with this license"
    },
    "toasts": {
      "mixedTenantWarning": "Please select licenses from the same client to edit them together.",
      "dismiss": "Dismiss"
    }
  },
  "plans": {
    "title": "Plans",
    "subtitle": "Configure plan features and pricing",
    "addPlan": "Add Plan",
    "searchPlans": "Search plans...",
    "filterByType": "Filter by Type",
    "loading": "Loading plans...",
    "features": "Features:",
    "ofEnabled": "of {{total}} enabled",
    "created": "Created:",
    "featuresConfiguration": "Features Configuration",
    "noFeatures": "No features configured for this plan",
    "noPlansFound": "No plans found",
    "adjustFilters": "Try adjusting your filters",
    "noPlansCreated": "No plans have been created yet",
    "dialog": {
      "addTitle": "Add Plan",
      "editTitle": "Edit Plan",
      "planName": "Plan Name",
      "planStatus": "Plan Status",
      "description": "Description",
      "planType": "Plan Type",
      "maxUsers": "Max Users",
      "validFrom": "Valid From",
      "validTo": "Valid To",
      "featureConfiguration": "Feature Configuration",
      "noFeatures": "No features available",
      "planFeatures": "Plan Features",
      "planFeaturesSubtitle": "Manage which features are included in this plan",
      "availableFeatures": "Available Features",
      "willBeAdded": "Will be added",
      "willBeRemoved": "Will be removed",
      "removeFromPlan": "Remove from plan",
      "noFeaturesAssigned": "No features assigned to this plan",
      "allFeaturesAdded": "All features have been added to this plan",
      "maxUsersWarning": "{{count}} license(s) currently exceed the Max Users limit. Existing licenses will not be affected."
    },
    "deleteDialog": {
      "title": "Delete Plan",
      "checking": "Checking for dependencies...",
      "cannotDelete": "Cannot delete this plan",
      "inUseBy": "is currently in use by",
      "license": "license",
      "licenses": "licenses",
      "reassignFirst": "Please reassign or remove these licenses before deleting this plan.",
      "affectedLicenses": "Affected Licenses:",
      "confirmTitle": "Delete \"{{planName}}\"?",
      "confirmMessage": "This plan has no active license dependencies. This action cannot be undone.",
      "deletePlan": "Delete Plan"
    }
  },
  "roles": {
    "title": "Roles & Permissions",
    "subtitle": "Manage platform user roles and configure access control scoping",
    "type": {
      "governance": "Governance",
      "operational": "Operational",
      "custom": "Custom"
    },
    "splitPanel": {
      "title": "Roles Split Panel View",
      "subtitle": "Users on the left, role assignment on the right",
      "searchUsers": "Search users...",
      "emptyStateTitle": "No User Selected",
      "emptyStateDesc": "Select a user from the list on the left to manage their role assignments.",
      "saveChanges": "Save Changes",
      "saving": "Saving…",
      "reset": "Reset",
      "rolesAssigned": "{{count}} roles assigned",
      "roleAssigned": "1 role assigned",
      "noRolesAssigned": "No roles assigned",
      "viewRoleDetails": "View role details",
      "assignedUsers": "Assigned Users ({{count}})",
      "assignedUsersPartial": "Users with this role ({{count}} shown from loaded list)",
      "assignedUsersOverflow": "+ {{count}} more not shown",
      "noUsersAssigned": "No users currently assigned to this role.",
      "permissionsHierarchy": "Permissions Hierarchy",
      "noPermissions": "No permissions associated with this role.",
      "descriptionHeader": "Description",
      "noDescription": "No description provided.",
      "loadingUsers": "Loading users…",
      "loadMore": "Load more",
      "noUsersMatchSearch": "No users match search",
      "saveSuccess": "Role assignments saved successfully.",
      "savePartial": "{{succeeded}} change(s) succeeded, {{failed}} failed.",
      "saveError": "Failed to save changes.",
      "saveUnauthorized": "You do not have permission to make one or more of those role changes.",
      "loadError": "Error loading roles settings.",
      "globalAdminRoleReadOnly": "Only a Global Admin can assign or remove the Global Admin role.",
      "rolesReadOnly": "You have view-only access to roles. Contact a Tenant Admin to request changes.",
      "permissionsPreviewNotice": "Permission list is preview data until the roles API exposes per-role permissions.",
      "bulk": {
        "enterBulk": "Bulk assign roles",
        "exitBulk": "Exit bulk mode",
        "singleMode": "Individual selection — select a user to edit roles",
        "selectAll": "Select all",
        "selectedCount": "{{count}} users selected",
        "title": "Bulk Assign Roles",
        "emptyStateTitle": "No Users Selected",
        "selectUsersHint": "Select one or more users from the list on the left to assign roles.",
        "clearSelection": "Clear selection",
        "applyButton": "Apply to {{count}} users",
        "nothingToAssign": "No changes to apply for the selected users.",
        "applySuccess": "Applied {{count}} role change(s) across {{users}} users.",
        "applyPartial": "{{succeeded}} change(s) succeeded, {{failed}} failed.",
        "applyError": "Failed to apply role changes.",
        "dialog": {
          "title": "Confirm Role Changes",
          "message": "The following changes will be applied to {{count}} users:",
          "assigning": "Assigning",
          "removing": "Removing",
          "confirm": "Apply Changes"
        }
      },
      "filter": {
        "buttonTooltip": "Filter and sort users",
        "buttonAriaLabel": "Filter and sort users",
        "buttonAriaLabelWithCount": "Filter and sort users, {{count}} active",
        "title": "Filters & Sort",
        "clearAll": "Clear all",
        "sortBy": "Sort by",
        "sortName": "Name",
        "sortEmail": "Email",
        "sortRole": "Role",
        "sortAscName": "↑ A-Z",
        "sortDescName": "↓ Z-A",
        "sortAscEmail": "↑ A-Z",
        "sortDescEmail": "↓ Z-A",
        "sortAscRole": "↑ A-Z",
        "sortDescRole": "↓ Z-A",
        "roleCategory": "Role",
        "governance": "Governance",
        "operational": "Operational",
        "custom": "Custom",
        "status": "Status",
        "statusAll": "All",
        "statusHasRole": "Has role",
        "statusNoRole": "No role",
        "includeCrossTenant": "All tenants",
        "usersShown": "{{shown}} users shown",
        "usersShownWithMore": "{{shown}} users shown (more available)"
      }
    },
    "dialog": {
      "governanceRolesHeader": "Governance Roles",
      "governanceRolesBadge": "Administrative",
      "operationalRolesHeader": "Operational Roles",
      "operationalRolesBadge": "Day-to-day access",
      "customRolesHeader": "Custom Roles",
      "customRolesBadge": "Tenant-specific",
      "noRolesAvailable": "No roles available"
    }
  }
}
